// Catalog.ID Client — Public Profile Lookup (Pre-Auth)
// ============================================================================
// Standalone view for unauthenticated visitors to look up a public profile by CID.
// Accessible at /profile/:cid or as a search form.

import { createElement, createFieldGroup, createFormRow, createEmptyState, escapeHtml } from '@catalog-shell';
import * as profileApi from '../api/profile';
import type { PublicProfile } from '../api/types';
import { claimTypeLabel } from '../pane-types/claims-list-pane';

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
    natural_person: 'Personal',
    organization: 'Organization',
    delegate: 'Delegate',
    agent: 'Agent',
};

export function renderProfileLookup(container: HTMLElement, cid?: string): void {
    container.innerHTML = '';

    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card' });
    card.style.width = '520px';

    const title = createElement('h2', { className: 'cid-auth-title', textContent: 'Catalog.ID Profile' });
    card.appendChild(title);

    if (cid) {
        // Load profile directly
        const contentEl = createElement('div');
        card.appendChild(contentEl);
        void loadAndRenderProfile(contentEl, cid, container);
    } else {
        // Show search form
        renderSearchForm(card, container);
    }

    wrapper.appendChild(card);
    container.appendChild(wrapper);
}

function renderSearchForm(card: HTMLElement, rootContainer: HTMLElement): void {
    const group = createFieldGroup({ title: 'Look Up Identity' });

    const cidRow = createFormRow({ label: 'Catalog.ID' });
    const input = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
    input.type = 'text';
    input.placeholder = 'e.g., abc1234efg';
    cidRow.content.appendChild(input);
    group.body.appendChild(cidRow.element);

    const actionRow = createFormRow({ label: '' });
    const lookupBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Look Up',
    });
    lookupBtn.addEventListener('click', () => {
        const val = input.value.trim();
        if (!val) return;
        // Navigate to /profile/:cid
        window.history.pushState(null, '', `/profile/${encodeURIComponent(val)}`);
        renderProfileLookup(rootContainer, val);
    });
    actionRow.content.appendChild(lookupBtn);
    group.body.appendChild(actionRow.element);

    card.appendChild(group.element);
}

async function loadAndRenderProfile(
    container: HTMLElement,
    cid: string,
    rootContainer: HTMLElement,
): Promise<void> {
    container.innerHTML = '';
    container.appendChild(createElement('div', {
        className: 'cid-wizard-status',
        textContent: 'Loading profile...',
    }));

    let profile: PublicProfile;
    try {
        profile = await profileApi.getPublicProfile(cid);
    } catch {
        container.innerHTML = '';
        container.appendChild(createEmptyState({
            message: 'Profile Not Found',
            description: `No public profile exists for "${escapeHtml(cid)}".`,
        }));
        addBackLink(container, rootContainer);
        return;
    }

    container.innerHTML = '';

    // Identity info
    const infoGroup = createFieldGroup({ title: 'Identity' });

    const cidRow = createFormRow({ label: 'Catalog.ID' });
    cidRow.content.appendChild(createElement('span', {
        className: 'cid-profile-cid',
        textContent: profile.cid,
    }));
    infoGroup.body.appendChild(cidRow.element);

    const typeRow = createFormRow({ label: 'Type' });
    typeRow.content.appendChild(createElement('span', {
        textContent: ACCOUNT_TYPE_LABELS[profile.accountType] ?? profile.accountType,
    }));
    infoGroup.body.appendChild(typeRow.element);

    if (profile.organizationCid) {
        const orgRow = createFormRow({ label: 'Organization' });
        const orgLink = createElement('a', {
            className: 'cid-profile-link',
            textContent: profile.organizationCid,
        });
        orgLink.setAttribute('href', `/profile/${profile.organizationCid}`);
        orgLink.addEventListener('click', (e) => {
            e.preventDefault();
            window.history.pushState(null, '', `/profile/${profile.organizationCid}`);
            renderProfileLookup(rootContainer, profile.organizationCid!);
        });
        orgRow.content.appendChild(orgLink);
        infoGroup.body.appendChild(orgRow.element);
    }

    if (profile.principalCid) {
        const principalRow = createFormRow({ label: 'Principal' });
        const principalLink = createElement('a', {
            className: 'cid-profile-link',
            textContent: profile.principalCid,
        });
        principalLink.setAttribute('href', `/profile/${profile.principalCid}`);
        principalLink.addEventListener('click', (e) => {
            e.preventDefault();
            window.history.pushState(null, '', `/profile/${profile.principalCid}`);
            renderProfileLookup(rootContainer, profile.principalCid!);
        });
        principalRow.content.appendChild(principalLink);
        infoGroup.body.appendChild(principalRow.element);
    }

    container.appendChild(infoGroup.element);

    // Published claims
    if (profile.claims.length === 0) {
        const emptyGroup = createFieldGroup({ title: 'Published Claims' });
        emptyGroup.body.appendChild(createEmptyState({
            message: 'No public claims',
            description: 'This identity has no published claims.',
        }));
        container.appendChild(emptyGroup.element);
    } else {
        const claimsGroup = createFieldGroup({ title: 'Published Claims' });
        for (const claim of profile.claims) {
            const row = createFormRow({ label: claimTypeLabel(claim.claimType) });
            row.content.appendChild(createElement('span', {
                textContent: claim.publicValue,
            }));
            claimsGroup.body.appendChild(row.element);
        }
        container.appendChild(claimsGroup.element);
    }

    addBackLink(container, rootContainer);
}

function addBackLink(container: HTMLElement, rootContainer: HTMLElement): void {
    const backRow = createElement('div', { className: 'cid-pane-note' });
    const backLink = createElement('a', {
        className: 'cid-profile-link',
        textContent: '← Search another identity',
    });
    backLink.setAttribute('href', '#');
    backLink.addEventListener('click', (e) => {
        e.preventDefault();
        window.history.pushState(null, '', '/profile');
        renderProfileLookup(rootContainer);
    });
    backRow.appendChild(backLink);
    container.appendChild(backRow);
}
